import React, { useContext, useMemo, useState } from 'react';

import { RadioGroup } from '@mui/material';
import { FormikProps } from 'formik';

// components
import ErrorSnackBar from '../../../components/errors/ErrorSnackBar';
// models
import { SearchableSelectType } from '../../../components/inputs/searchableSelect';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../context/EditorContext';

// helpers
import {
    formatDateForDB,
    formatISODate,
    getUTCDate,
    setDateToStartOfDay,
} from '../../../helpers/dateTimeHelpers';
import { getFormulaSchedulingType } from '../../../helpers/formulaHelpers';
import {
    getUtcTimeToZonedTime,
    getZonedTimeToUtcTime,
} from '../../../helpers/timezoneHelper';

// hooks
import useFormulaTimeSlotsMutation from '../../../hooks/formulas/useFormulaTimeSlotMutation';
import useFormulaUpdateBaseMutation from '../../../hooks/formulas/useFormulaUpdateBaseMutation';
import useAnalytics from '../../../hooks/helper/useAnalytics';

import {
    FormulaDetailModel,
    FormulaSchedulingOptions,
    FormulaSchedulingOptionsLabels,
    FormulaViewSetupFormValue,
    ApprovedFormulaState,
} from '../../../models/formula.model';
import { TimingSlotsModel } from '../../../models/timing-slots.model';

import AutomatedDeliveryWithConstraintsSection from './AutomatedDeliveryWithConstraintsSection';
import ControlledDeliverySection from './ControlledDeliverySection';
import DeliveryTypeInfoSection from './DeliveryTypeInfoSection';
import FormulaDeliveryTypeContainer from './FormulaDeliveryTypeContainer';
import FrequencyProviderSettings from './FrequencyProviderSettings';

interface FormulaDeliverySettingsProps {
    data?: FormulaDetailModel;
    isDisabled?: boolean;
    timingSlots: TimingSlotsModel[];
    formikProps?: FormikProps<FormulaViewSetupFormValue>;
}

const FormulaDeliverySettings: React.FC<
    React.PropsWithChildren<FormulaDeliverySettingsProps>
> = ({ data, isDisabled, timingSlots, formikProps }) => {
    const { setEditorDetails, editorDetails } = useContext(EditorContext);
    const { track } = useAnalytics();
    const updateFormulaMutation = useFormulaUpdateBaseMutation(
        data?.id as number,
    );

    const formulaId = data?.id;

    const { mutate } = useFormulaTimeSlotsMutation(formulaId as number);

    const [schedulingType, setSchedulingType] =
        useState<FormulaSchedulingOptions>(
            getFormulaSchedulingType(
                timingSlots,
                formikProps ? (formikProps.values as any) : data,
            ),
        );

    const [startDate, setStartDate] = useState<Date | null>(
        !data?.is_controlled_message && data?.send_start_date
            ? getUTCDate(data?.send_start_date)
            : null,
    );

    const [controlledSendDate, setControlledSendDate] = useState<Date | null>(
        data?.is_controlled_message && data?.timezone && data?.send_start
            ? getUtcTimeToZonedTime(data.timezone, data.send_start)
            : null,
    );

    const [stopDate, setStopDate] = useState<Date | null>(
        !data?.is_controlled_message && data?.send_stop_date
            ? getUTCDate(data?.send_stop_date)
            : null,
    );
    const [timezone, setTimeZone] = useState<string>(data?.timezone as string);

    const isControlledFormula = useMemo<boolean>(
        () => schedulingType === FormulaSchedulingOptions.CONTROLLED,
        [schedulingType],
    );

    const getValidatedDate = (date: Date | null) => {
        if (date) {
            return formatISODate(setDateToStartOfDay(date));
        }
        return null;
    };

    const resetSchedulingSettings = () => {
        const schedulingType = getFormulaSchedulingType(timingSlots, data);
        if (schedulingType === FormulaSchedulingOptions.CONTROLLED) {
            setStartDate(null);
            setStopDate(null);
            setControlledSendDate(getUTCDate(data?.send_start, true));
        } else if (
            schedulingType ===
            FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS
        ) {
            setStartDate(getUTCDate(data?.send_start_date));
            setStopDate(getUTCDate(data?.send_stop_date));
        } else {
            setStartDate(null);
            setStopDate(null);
            setControlledSendDate(null);
        }
        setSchedulingType(schedulingType);
        setTimeZone(data?.timezone as string);
    };

    const saveHandler = (
        schedulingType: FormulaSchedulingOptions,
        startDate: Date | null,
        stopDate: Date | null,
        timezone?: string,
    ) => {
        const isControlledFormula =
            schedulingType === FormulaSchedulingOptions.CONTROLLED;
        const isAutomatedFormula =
            schedulingType === FormulaSchedulingOptions.AUTOMATED;
        const controlledTimeZonedValue = getZonedTimeToUtcTime(
            timezone as string,
            startDate as Date,
        );

        if (formikProps) {
            formikProps?.setFieldValue(
                'send_start',
                isControlledFormula ? controlledTimeZonedValue : null,
            );
            formikProps?.setFieldValue('send_stop', null);
            formikProps?.setFieldValue(
                'send_start_date',
                !isControlledFormula && !isAutomatedFormula && startDate
                    ? formatDateForDB(startDate)
                    : null,
            );
            formikProps?.setFieldValue(
                'send_stop_date',
                !isControlledFormula && !isAutomatedFormula && stopDate
                    ? formatDateForDB(stopDate)
                    : null,
            );
            formikProps?.setFieldValue(
                'is_controlled_message',
                isControlledFormula,
            );

            formikProps?.setFieldValue('timezone', timezone);
        } else {
            updateFormulaMutation.mutate(
                {
                    send_start: isControlledFormula
                        ? controlledTimeZonedValue
                        : null,
                    send_stop: null,
                    send_start_date:
                        !isControlledFormula && !isAutomatedFormula && startDate
                            ? formatDateForDB(startDate)
                            : null,
                    send_stop_date:
                        !isControlledFormula && !isAutomatedFormula && stopDate
                            ? formatDateForDB(stopDate)
                            : null,
                    is_controlled_message: isControlledFormula,
                    timezone,
                    id: formulaId as number,
                },
                {
                    onSuccess: () => {
                        if (!isControlledFormula) {
                            track('formula_start_stop_dates_updated', {
                                formula_id: formulaId,
                                new_stop: getValidatedDate(stopDate),
                                new_start: getValidatedDate(startDate),
                            });
                        } else {
                            track(
                                'controlled_formula_start_date_time_updated',
                                {
                                    formula_id: formulaId,
                                    new_start: getValidatedDate(startDate),
                                },
                            );
                        }
                        setEditorDetails({
                            ...editorDetails,
                            isControlledFormula: isControlledFormula,
                        });
                        if (isControlledFormula || isAutomatedFormula) {
                            setStartDate(null);
                            setStopDate(null);
                        }
                        if (!isControlledFormula) {
                            setControlledSendDate(null);
                        }
                    },
                    onError: resetSchedulingSettings,
                },
            );
        }

        if (schedulingType === FormulaSchedulingOptions.AUTOMATED) {
            const selectedIds = timingSlots?.map((slot) => slot.id);
            if (selectedIds) {
                if (formikProps) {
                    setStartDate(null);
                    setStopDate(null);
                    formikProps.setFieldValue('send_start_date', null);
                    formikProps.setFieldValue('time_slot_ids', selectedIds);
                } else {
                    mutate({
                        time_slot_ids: selectedIds,
                    });
                }
            }
        }
    };

    const scheduleTypeChangeHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ) => {
        setSchedulingType(value as FormulaSchedulingOptions);
        if (formikProps) {
            formikProps.setFieldValue('scheduling_type', value);
        }
        const isControlledFormula =
            value === FormulaSchedulingOptions.CONTROLLED;
        const isAutomatedFormula = value === FormulaSchedulingOptions.AUTOMATED;
        const isAutomatedGuardrailsFormula =
            value === FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS;
        let _startDate = startDate;
        if (isAutomatedGuardrailsFormula && !startDate) {
            _startDate = new Date();
            setStartDate(_startDate);
        }
        saveHandler(
            value as FormulaSchedulingOptions,
            isControlledFormula
                ? controlledSendDate
                : isAutomatedFormula
                ? null
                : _startDate,
            isAutomatedFormula || isControlledFormula ? null : stopDate,
            data?.timezone,
        );
    };

    const startDateHandler = (startDate: Date | null) => {
        setStartDate(startDate);
        saveHandler(schedulingType, startDate, stopDate, data?.timezone);
    };

    const stopDateHandler = (stopDate: Date | null) => {
        setStopDate(stopDate);
        saveHandler(schedulingType, startDate, stopDate, data?.timezone);
    };

    const controlledStartDateHandler = (controlledSendDate: Date | null) => {
        setControlledSendDate(controlledSendDate);
        saveHandler(schedulingType, controlledSendDate, null, data?.timezone);
    };

    const isControlledWithLive = useMemo<boolean>(
        () =>
            !!(
                data?.is_controlled_message &&
                data?.active_state &&
                [
                    ApprovedFormulaState.Live,
                    ApprovedFormulaState.Scheduled,
                ].includes(data?.active_state as ApprovedFormulaState)
            ),
        [data],
    );

    const isControlledFormulaDisable = useMemo<boolean>(
        () =>
            schedulingType !== FormulaSchedulingOptions.CONTROLLED ||
            !!isDisabled,
        [schedulingType, isDisabled],
    );

    const isAutomatedFormulaDisable = useMemo<boolean>(
        () =>
            schedulingType !==
                FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS ||
            !!isDisabled,
        [schedulingType, isDisabled],
    );

    const onTimezoneChanged = (value: SearchableSelectType) => {
        setTimeZone(value.value as string);
        saveHandler(
            schedulingType,
            controlledSendDate,
            null,
            value.value as string,
        );
    };

    return (
        <MainContainer>
            <RadioGroup
                name="formula-type"
                value={schedulingType}
                onChange={scheduleTypeChangeHandler}
            >
                <Container>
                    <FormulaDeliveryTypeContainer
                        value={schedulingType}
                        schedulingType={FormulaSchedulingOptions.AUTOMATED}
                        title={
                            FormulaSchedulingOptionsLabels[
                                FormulaSchedulingOptions.AUTOMATED
                            ]
                        }
                        isAutomated
                        disabled={isDisabled || isControlledWithLive}
                    >
                        <DeliveryTypeInfoSection description="Your agents determine the ideal message and message timing for each of their respective users and schedule messages for them at that time. Messages will start to be sent the day after the message group goes live." />
                    </FormulaDeliveryTypeContainer>

                    <FormulaDeliveryTypeContainer
                        disabled={isDisabled || isControlledWithLive}
                        value={schedulingType}
                        schedulingType={
                            FormulaSchedulingOptions.AUTOMATED_WITH_GUARDRAILS
                        }
                        title={
                            FormulaSchedulingOptionsLabels[
                                FormulaSchedulingOptions
                                    .AUTOMATED_WITH_GUARDRAILS
                            ]
                        }
                    >
                        <AutomatedDeliveryWithConstraintsSection
                            isControlledFormula={isControlledFormula}
                            isDisabled={isAutomatedFormulaDisable}
                            start={startDate}
                            setStart={startDateHandler}
                            stop={stopDate}
                            setStop={stopDateHandler}
                            formula={data}
                            schedulingType={schedulingType}
                            formikProps={formikProps}
                        />
                    </FormulaDeliveryTypeContainer>

                    <FormulaDeliveryTypeContainer
                        disabled={
                            isDisabled ||
                            (!!formikProps && !isControlledWithLive)
                        }
                        value={schedulingType}
                        schedulingType={FormulaSchedulingOptions.CONTROLLED}
                        title={
                            FormulaSchedulingOptionsLabels[
                                FormulaSchedulingOptions.CONTROLLED
                            ]
                        }
                    >
                        <ControlledDeliverySection
                            startDate={controlledSendDate}
                            isDisabled={isControlledFormulaDisable}
                            setStartDate={controlledStartDateHandler}
                            onTimezoneChanged={onTimezoneChanged}
                            timezoneValue={timezone}
                        />
                    </FormulaDeliveryTypeContainer>
                </Container>
            </RadioGroup>

            <FrequencyProviderSettings
                overrideProviderFrequencyLimits={
                    !!data?.override_provider_frequency_limits
                }
                isDisabled={isDisabled}
            />

            {updateFormulaMutation.isError && (
                <ErrorSnackBar title={'Updating date failed.'} />
            )}
        </MainContainer>
    );
};

const MainContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    width: '100%',
});

const Container = styled.div({
    display: `flex`,
    flexDirection: 'column',
    backgroundColor: aampeTheme.colors.white,
    color: aampeTheme.colors.gray800,
    fontSize: '14px',
    fontWeight: 400,
    width: '100%',
    gap: aampeTheme.spacing(2),
});

export default FormulaDeliverySettings;
